import { CustomizableInput } from 'components/common-form/builder/form/node/field/Grid/cells/CustomizableInput';
import { useBlackoutDates } from 'components/common-form/hooks/useBlackoutDates';
import { Amount } from 'components/shared/Amount/Amount';
import { DatePicker } from 'components/shared/DatePicker/DatePicker';
import { MonoDatePickerWithDefaultPreset } from 'components/shared/DatePicker/MonoDatePicker';
import { ListField } from 'components/shared/ListField/ListField';
import { NumberFormatter } from 'components/shared/NumberFormatter/NumberFormatter';
import { PercentWithUnit } from 'components/shared/Percent/PercentWithUnit';
import { useUserNotification } from 'hooks/useUserNotification';
import type { CellMetadata, FieldMetadata } from 'models/forms/fields';
import { FC, KeyboardEvent, useCallback, useContext } from 'react';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';

import type { Navigation } from '../GridField.model';
import type { OnKeyDownArg } from '../Keys';
import { getColumnCount, getPastedData, parseTsvGrid } from '../pasting';
import { EditableTextCell } from './EditableTextCell';
import { useLostInput } from './utils';
import { WithCurrency } from './WithCurrency';

interface EditableGridCellProps {
  cellId: string;
  e2e: string;
  columnIndex: number;
  rowIndex: number;
  metadata: FieldMetadata;
  value: unknown;
  lostInputBeforeEditing?: KeyboardEventInit;
  customizedData?: CellMetadata;
  onValueChanged: (row: number, cellId: string, value: unknown) => void;
  onPaste?: (rowIndex: number, columnIndex: number, values: string[][]) => void;
  onEditionCancelled: () => void;
  onNavigated: (navigation: Navigation, isEditing?: boolean) => void;
  tabIndex?: number;
}

export const EditableGridCell: FC<EditableGridCellProps> = ({
  cellId,
  e2e,
  rowIndex,
  columnIndex,
  metadata,
  value = null,
  customizedData,
  onValueChanged,
  onPaste,
  onEditionCancelled,
  onNavigated,
  lostInputBeforeEditing,
  tabIndex,
}) => {
  const { id: columnId } = metadata;
  const isCustomized = customizedData?.isCustomized ?? false;
  const notification = useUserNotification();
  const logAnalyticsCategory = useContext(WorkspaceAnalyticCategoryContext);
  const blackoutDates = useBlackoutDates(metadata.type === 'DateField' ? metadata.calendars : undefined);

  const lostInputValue = useLostInput(metadata.type, value, lostInputBeforeEditing?.key);

  const onChange = useCallback(
    (newValue: unknown = null) => {
      if (newValue !== value) {
        onValueChanged(rowIndex, columnId, newValue);
      }
      onEditionCancelled();
    },
    [value, onEditionCancelled, onValueChanged, rowIndex, columnId],
  );

  const onPasteEvent = useCallback(
    (pastedData: string | undefined) => {
      if (onPaste === undefined) {
        return;
      }
      logAnalytics.trackEvent(logAnalyticsCategory, 'Paste in Grid');
      try {
        const pastedValues = parseTsvGrid(pastedData);
        const columnCount = getColumnCount(pastedValues);
        if (pastedValues !== undefined && columnCount > 0) {
          onPaste(rowIndex, columnIndex, pastedValues);
          onEditionCancelled();
        }
      } catch (err: any) {
        notification.error({
          title: { id: 'toast.paste' },
          content: [{ id: 'moreMenu.pasteFromClipboard.error', values: { message: err.message } }],
        });
      }
    },
    [onPaste, logAnalyticsCategory, rowIndex, columnIndex, onEditionCancelled, notification],
  );

  const onPasteClipboardEvent = useCallback(
    (e: ClipboardEvent) => {
      const pastedData = getPastedData(e);
      onPasteEvent(pastedData);
    },
    [onPasteEvent],
  );
  const onKeyDown = useCallback(
    ({ e, commit }: OnKeyDownArg) => {
      switch (e.key) {
        case 'Escape':
          {
            onEditionCancelled();
          }
          break;
        case 'Enter':
          {
            commit();
            onNavigated('down', false);
          }
          break;
        case 'Tab':
          {
            commit();
            onNavigated('next', true);
          }
          break;
        default:
          break;
      }
    },
    [onEditionCancelled, onNavigated],
  );
  const onNumberKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const arg: OnKeyDownArg = {
        e: e,
        commit: () => {
          (e.target as HTMLInputElement).blur();
        },
      };
      onKeyDown(arg);
    },
    [onKeyDown],
  );

  switch (metadata.type) {
    case 'PercentField':
      return (
        <PercentWithUnit
          id={cellId}
          e2e={e2e}
          unit={metadata.unit}
          unitClassName={isCustomized ? 'text-info' : ''}
          precision={metadata.nbDecimals}
          isReadOnly={metadata.isReadOnly}
          lostInputValue={lostInputValue}
          onBlur={onChange}
          onPaste={onPasteClipboardEvent}
          isEdited={isCustomized}
          isFocused
          onKeyDown={onNumberKeyDown}
          tabIndex={tabIndex}
        />
      );
    case 'DateField': {
      return (
        <div className="input-group flex-nowrap">
          {isCustomized && (
            <div className="input-group-text ">
              <i className="icon text-info  icon-sm">fiber_manual_record</i>
            </div>
          )}
          <MonoDatePickerWithDefaultPreset
            id={cellId}
            e2e={e2e}
            minDate={metadata.startDate}
            maxDate={metadata.endDate}
            allowWeekend={metadata.allowWeekend}
            disabled={metadata.isReadOnly ?? false}
            isClearable={false}
            value={value as Nullable<string>}
            inputValue={lostInputValue}
            onChange={onChange}
            blackoutDates={blackoutDates}
            onPaste={onPasteEvent}
            onKeyDown={onKeyDown}
            tabIndex={tabIndex}
          />
        </div>
      );
    }
    case 'DatesListField': {
      return (
        <div className="input-group">
          <DatePicker
            type="multi"
            id={cellId}
            minDate={metadata.startDate}
            maxDate={metadata.endDate}
            allowWeekend={metadata.allowWeekend}
            disabled={metadata.isReadOnly ?? false}
            isClearable
            values={value as readonly string[] | undefined}
            inputValue={lostInputValue}
            onChange={onChange}
            blackoutDates={blackoutDates}
            onKeyDown={onKeyDown}
            tabIndex={tabIndex}
          />
          {isCustomized && (
            <div className="input-group-text" style={{ right: '30px' }}>
              <i className="icon text-info  icon-sm">fiber_manual_record</i>
            </div>
          )}
        </div>
      );
    }
    case 'TextField':
      return (
        <EditableTextCell
          id={cellId}
          e2e={e2e}
          value={value as string}
          onBlur={onChange}
          onKeyDown={onNumberKeyDown}
          onPaste={onPasteClipboardEvent}
          tabIndex={tabIndex}
        />
      );
    case 'AmountField':
      return (
        <WithCurrency currency={metadata.currency} isCustomized={isCustomized}>
          <Amount
            id={cellId}
            e2e={e2e}
            className="form-control clickable"
            precision={metadata.nbDecimals}
            isReadOnly={metadata.isReadOnly}
            lostInputValue={lostInputValue}
            onBlur={onChange}
            onPaste={onPasteClipboardEvent}
            isEdited={isCustomized}
            isFocused
            onKeyDown={onNumberKeyDown}
            tabIndex={tabIndex}
          />
        </WithCurrency>
      );
    case 'NumberField':
      return (
        <NumberFormatter
          id={cellId}
          e2e={e2e}
          className="form-control clickable"
          precision={metadata.nbDecimals}
          decimalPadding={metadata.decimalPadding}
          isReadOnly={metadata.isReadOnly}
          lostInputValue={lostInputValue}
          onBlur={onChange}
          onPaste={onPasteClipboardEvent}
          isEdited={isCustomized}
          isFocused
          onKeyDown={onNumberKeyDown}
          tabIndex={tabIndex}
        />
      );
    case 'ListField':
      return (
        <CustomizableInput isCustomized={isCustomized}>
          <ListField
            id={cellId}
            items={metadata.availableItems}
            value={value as string | undefined}
            isReadOnly={metadata.isReadOnly}
            isEdited={isCustomized}
            onChange={e => onChange(e.target.value)}
          />
        </CustomizableInput>
      );
    default:
      throw new Error(`"${metadata.type}" cell type is  not supported and therefore should not be editable.`);
  }
};

EditableGridCell.displayName = 'EditableGridCell';
